import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMutation, gql } from "@apollo/client";
import { Tab } from "@headlessui/react";
import clsx from "clsx";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import DataTable from "components/DataTable";
import Select from "components/Select";
import StatusModal from "components/StatusModal";
import StatusTabs from "components/StatusTabs";
import { BROKER_FR_STATUS_LABELS, CARRIER_FR_STATUS_LABELS } from "constants/base";
import { FR_CONTRACT_TYPE } from "constants/haulpay-broker";
import { CARRIER_NOA_STATUS_HEADER, MANAGE_PAY_TABLE_HEADER_LIST } from "./tableHeaders";
import { HISTORY_TABLE_HEADER_LIST } from "constants/haulpay-carrier";
import { routes } from "constants/routes";
import {
  NEW_FUNDING_REQUEST_TYPE,
  NEW_STATUS_LIST
} from "features/main/HaulPay/modules/Broker/ManagePayList";
import {
  MUTATION_UPDATE_BROKER_FUNDING_REQUEST_STATUS,
  MUTATION_UPDATE_BROKER_FUNDING_REQUEST_TYPE
} from "features/main/Loads/Post/FundingRequest/query";
import { parseError } from "helpers/convertFormData";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { useColorScheme } from "hooks/useColorScheme";
import { usePullToRefresh } from "context/PullToRefreshContext";
import usePaginatedQuery from "hooks/usePaginatedQuery";
import { useUserFeature } from "hooks/useUserFeature";
import { IOption } from "models/Form";
import { IFundingRequestItem, ISummaryData } from "models/HaulPay";
import { selectFactoringProfile } from "store/user/selector";
import { Filters } from "../../Broker/ManagePay";
import { QUERY_FUNDING_REQUEST_BY_ME } from "../../Broker/ManagePay/query";
import { QUERY_CARRIER_FUNDING_REQUEST_BY_ME } from "../../Carrier/ManagePay/query";
import SearchInput from "./components/SearchInput";
import styles from "./style.module.scss";
import { selectAccount } from "../../../../../store/auth/selector";
import FRTypeFilterDropdown from "../FRTypeFilterDropdown";
import { useAnalytics } from "hooks/useAnalytics";
import Checkbox from "../../../../../components/Checkbox";
import Tooltip from "../../../../../components/Tooltip";

type ListByStatusDesktopProps = {
  summaryData: ISummaryData;
  debtor?: string | null;
  refetchSummaryData: (data: any) => void;
  debtorName?: string | null;
};

const PENDING_STATUS_INDEX = NEW_STATUS_LIST.findIndex(({ value }) => value === "Special Pending");

const NEW_STATUS_LIST_WITH_HOLD = [
  ...NEW_STATUS_LIST.slice(0, PENDING_STATUS_INDEX + 1),
  {
    id: NEW_STATUS_LIST.length + 1,
    label: "Hold",
    value: "Hold"
  },
  ...NEW_STATUS_LIST.slice(PENDING_STATUS_INDEX + 1)
];

type FormValues = {
  state: IOption;
  type: IOption;
};

const VALIDATE_FUNDING_REQUESTS = gql`
  mutation ValidateFundingRequests(
    $requestIds: [String!]!
    $contractType: String
    $status: String
  ) {
    validateFundingRequests(requestIds: $requestIds, contractType: $contractType, status: $status) {
      results {
        id
        valid
        errors
      }
    }
  }
`;
type ValidateBrokerFundingRequestsVariables = {
  requestIds: string[];
  contractType?: string;
  status?: string;
};

function CustomSelectAllCheckbox({
  data,
  selectedList,
  selectAllLimit,
  handleSelectAll
}: {
  data: IFundingRequestItem[];
  selectedList: string[];
  selectAllLimit: number;
  handleSelectAll: () => void;
}) {
  return (
    <Tooltip
      handleMouseOver={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.currentTarget.style.position = "fixed";
        event.currentTarget.style.marginTop = "-10px";
      }}
      handleMouseOut={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.currentTarget.style.position = "";
        event.currentTarget.style.marginTop = "";
      }}
      position="right"
      message={
        <div className="flex flex-col gap-2">
          <span>You can only bulk update 50 funding requests at a time</span>
        </div>
      }>
      <Checkbox
        checked={data.length > 0 && selectedList?.length === Math.min(selectAllLimit, data.length)}
        onChange={() => handleSelectAll()}
      />
    </Tooltip>
  );
}

function CustomTooltipCheckbox({
  rowId,
  isSelected,
  handleChecked,
  isDisableCheckboxes,
  selectAllLimit
}: {
  rowId: string;
  isSelected: (id: string) => boolean;
  handleChecked: (id: string) => void;
  isDisableCheckboxes: boolean;
  selectAllLimit: number;
}) {
  return isDisableCheckboxes ? (
    // Do this only if select-all-limit is reached
    !isSelected(rowId) ? (
      // Only show tooltip checkbox if checkbox is not selected
      <Tooltip
        position="right"
        message={
          <div className="flex flex-col gap-2">
            <span>Limit reached ({selectAllLimit}). Deselect one to select another.</span>
          </div>
        }>
        <Checkbox checked={isSelected(rowId)} onChange={() => handleChecked(rowId)} />
      </Tooltip>
    ) : (
      <Checkbox checked={isSelected(rowId)} onChange={() => handleChecked(rowId)} />
    )
  ) : (
    // Default behavior when select-all-limit is not reached
    <Checkbox checked={isSelected(rowId)} onChange={() => handleChecked(rowId)} />
  );
}

export default function FRListByStatusDesktop({
  summaryData,
  debtor,
  refetchSummaryData,
  debtorName
}: Readonly<ListByStatusDesktopProps>) {
  const { currentIsDark } = useColorScheme();
  const { trackAllEvents } = useAnalytics();
  const { isBroker, isNonFactorRefactorEnabled } = useUserFeature();
  const FR_LABELS = isBroker ? BROKER_FR_STATUS_LABELS : CARRIER_FR_STATUS_LABELS;
  const navigate = useNavigate();
  const { userPermission } = useSelector(selectAccount);
  const factoringProfile = useSelector(selectFactoringProfile);
  const useNewCarrierFundingRequest = factoringProfile?.use_new_carrier_funding_requests ?? false;

  const carrierHeaders = useMemo(() => {
    const base = useNewCarrierFundingRequest
      ? HISTORY_TABLE_HEADER_LIST
      : HISTORY_TABLE_HEADER_LIST.filter((header) => header.value !== "contractType");
    const debtorIndex = base.findIndex((header) => header.value === "debtor.name");
    if (debtorIndex === -1) return [...base, CARRIER_NOA_STATUS_HEADER];
    return [
      ...base.slice(0, debtorIndex + 1),
      CARRIER_NOA_STATUS_HEADER,
      ...base.slice(debtorIndex + 1)
    ];
  }, [useNewCarrierFundingRequest]);
  const sortedSummary = useMemo(() => {
    return Object.keys(FR_LABELS).reduce<Record<string, any>>((acc, key) => {
      if (summaryData[key]) {
        acc[key] = summaryData[key];
      }
      return acc;
    }, {});
  }, [summaryData]);
  const defaultStatus =
    "incomplete" in sortedSummary ? "incomplete" : Object.keys(sortedSummary)[0] ?? "incomplete";
  const [filters, setFilters] = useState<Filters>({
    debtor: debtor ?? null,
    debtorName: debtorName ?? null,
    orderBy: "-created",
    status: defaultStatus,
    contractType: null
  });
  const [selectedFRs, setSelectedFRs] = useState<string[]>([]);
  const [errorList, setErrorList] = useState<string[]>([]);
  const [typeToUpdate, setTypeToUpdate] = useState<"status" | "type">("status");
  const [isSuccessModalOpen, toggleSuccessModal] = useToggle();
  const [clearFrTypeFilter, setClearFrTypeFilter] = useState(false);
  const [disableCheckboxes, setDisableCheckboxes] = useState(false);

  const SELECT_ALL_LIMIT = 50;

  const {
    control,
    setValue,
    handleSubmit,
    getValues,
    formState: { isSubmitting }
  } = useForm<FormValues>({
    defaultValues: {
      state: NEW_STATUS_LIST[0],
      type: NEW_FUNDING_REQUEST_TYPE[0]
    }
  });

  const {
    data,
    loading,
    totalCount,
    error,
    loadNextPage,
    loadPreviousPage,
    setPageSize,
    goToPage,
    pageInfo,
    resetPagination,
    refetchCurrentPage,
    refresh
  } = usePaginatedQuery<Record<string, any>, IFundingRequestItem>({
    query: isBroker ? QUERY_FUNDING_REQUEST_BY_ME : QUERY_CARRIER_FUNDING_REQUEST_BY_ME,
    variables: {
      debtor: filters.debtor,
      ...(filters.status !== "all" && { status: filters.status }),
      orderBy: filters.orderBy,
      loadOrInvoiceOrDebtorOrStatus: filters.loadOrInvoiceOrDebtorOrStatus,
      contractType: filters.contractType
    },
    cursorField: "fundingRequestsByMe",
    pageSize: isBroker ? 50 : 25,
    options: {
      notifyOnNetworkStatusChange: true,
      skip: !filters.status
    },
    loadMore: true
  });

  usePullToRefresh(refresh, { enabled: selectedFRs.length === 0 });

  useEffect(() => {
    if (error) showErrorMessage(parseError(error.message));
  }, [data, error, loading]);

  const [updateFRStatus] = useMutation(MUTATION_UPDATE_BROKER_FUNDING_REQUEST_STATUS, {
    onError(err) {
      err.graphQLErrors.forEach(({ message }) => showErrorMessage(parseError(message)));
    },
    onCompleted: () => {
      showSuccessMessage("Status updated successfully.");
    }
  });

  const [validateFRs] = useMutation(VALIDATE_FUNDING_REQUESTS, {
    onError: (err) => {
      if (err?.graphQLErrors)
        return err?.graphQLErrors.forEach(({ message }) => showErrorMessage(parseError(message)));
      if (Array.isArray(err)) return err.forEach((message) => showErrorMessage(message));
    },
    onCompleted: (data) => {
      const hasErrors = data.validateFundingRequests.results.some((r: any) => !r?.valid);
      if (hasErrors) {
        const results = data.validateFundingRequests.results || [];
        selectedFRs.map((fr, i) => {
          const frWithError = results.find((r: any) => r.id === fr && !r.valid);
          frWithError && setErrorList((prev) => [...prev, selectedFRs[i]]);
        });
      }
    }
  });

  const [updateFRType] = useMutation(MUTATION_UPDATE_BROKER_FUNDING_REQUEST_TYPE, {
    onError(err) {
      err.graphQLErrors.forEach(({ message }) => showErrorMessage(parseError(message)));
    },
    onCompleted: () => {
      showSuccessMessage("Type updated successfully.");
    }
  });

  const goToCreateLoad = useCallback(() => {
    // "send_rate_confirmation_remote_approved" really means
    // "DO_NOT_send_rate_confirmation_remote_approved" 🤷🏻‍♂️
    if (!factoringProfile?.send_rate_confirmation_remote_approved) {
      navigate(routes.LOADS_POSTED_CREATE);
    } else {
      navigate(routes.HAULPAY_BROKER_FUNDING_REQUEST);
    }
  }, [navigate]);

  const handleSelectAll = useCallback(() => {
    // Unselect all checkboxes if all visible are selected
    if (selectedFRs?.length >= data.length) {
      setSelectedFRs([]);
      setDisableCheckboxes(false);
    } else {
      // Select all FRs up to limit
      const frList: string[] = [];
      data.forEach((fr: IFundingRequestItem, index: number) => {
        if (index + 1 <= SELECT_ALL_LIMIT) {
          frList.push(fr.id);
        }
      });
      setSelectedFRs(frList);
      setDisableCheckboxes(frList.length >= SELECT_ALL_LIMIT);
    }
  }, [selectedFRs, data]);

  const toggleSelection = (id: string) => {
    // Uncheck checkbox
    if (selectedFRs.includes(id)) {
      setSelectedFRs(selectedFRs.filter((fr) => fr !== id));
      setDisableCheckboxes(false);
    } else {
      // Check checkbox only if not disabled
      if (!disableCheckboxes) {
        setSelectedFRs((prev) => {
          const newSelectedFRs = [...prev, id];
          // Disable rest of checkboxes if limit is reached
          if (newSelectedFRs.length === SELECT_ALL_LIMIT) {
            setDisableCheckboxes(true);
          }
          return newSelectedFRs;
        });
      }
    }
  };

  const handleSortBy = (key: string) => {
    resetPagination();
    setFilters((prev) => ({ ...prev, orderBy: key }));
  };

  const onUpdateFR = useCallback(
    async ({ state, type }: FormValues) => {
      if (state.value === "none" && type.value === "none") {
        showErrorMessage(
          typeToUpdate === "status"
            ? "Select Status from Dropdown and Try Again"
            : "Select Type from Dropdown and Try Again"
        );
        return;
      }
      const variables: ValidateBrokerFundingRequestsVariables = {
        requestIds: selectedFRs
      };
      if (state.value === "none") {
        variables.contractType = type.value;
      } else if (type.value === "none") {
        variables.status = state.value.toUpperCase().replace(/\s+/g, "_");
      }
      let response: any[] = [];
      try {
        setErrorList([]);

        const validationResponse = await validateFRs({
          variables
        });

        // Get valid FRs
        const validFRList = validationResponse?.data?.validateFundingRequests?.results
          ?.filter((r: any) => r?.valid)
          ?.map((r: any) => r.id);

        // Show error messages if any
        validationResponse?.data?.validateFundingRequests?.results
          ?.filter((r: any) => !r?.valid)
          ?.forEach((r: any) => {
            showErrorMessage(r.errors.join("\n"));
          });

        // No valid FRs so stop here
        if (!validFRList?.length) {
          return;
        }

        if (state.value === "none") {
          response = await Promise.all(
            validFRList.map((id: string) =>
              updateFRType({
                variables: {
                  id,
                  contractType: type.value,
                  selfFinanceRequested: type.label !== "Factored"
                }
              })
            )
          );
        } else if (type.value === "none") {
          response = await Promise.all(
            validFRList.map((id: string) =>
              updateFRStatus({
                variables: {
                  id,
                  status: state.value
                }
              })
            )
          );
        } else {
          showErrorMessage("Please select the correct item.");
        }
        if (response.length && response.find((item) => !item?.errors)) {
          setSelectedFRs([]);
          toggleSuccessModal();
          refetchSummaryData({ ...filters });
          await refetchCurrentPage();
        } else {
          for (let i = 0; i < validFRList.length; i++) {
            if (response[i]?.errors) {
              setErrorList((prev) => [...prev, validFRList[i]]);
            } else {
              setSelectedFRs((prev) => prev.filter((id) => id !== validFRList[i]));
            }
          }
        }
      } catch (error) {
        // Error handling is already managed in mutations
      } finally {
        // Reset checkbox "Limit reached" tooltips
        setDisableCheckboxes(false);
      }
    },
    [selectedFRs, updateFRStatus, updateFRType, toggleSuccessModal, typeToUpdate]
  );

  useEffect(() => {
    if (typeToUpdate === "status") {
      setValue("type", NEW_FUNDING_REQUEST_TYPE[0]);
    } else {
      setValue("state", NEW_STATUS_LIST[0]);
    }
  }, [typeToUpdate]);

  const updateFilter = useCallback(
    (key: keyof Filters, value: Filters[keyof Filters]) => {
      setFilters((prevFilters) => {
        if (prevFilters[key] === value) {
          return prevFilters;
        }
        return {
          ...prevFilters,
          [key]: value
        };
      });
    },
    [setFilters]
  );
  const handleSearch = useCallback(
    (value: string | null) => {
      refetchSummaryData({
        debtor: filters.debtor,
        loadOrInvoiceOrDebtorOrStatus: value
      });
      setFilters((prevFilters) => ({
        ...prevFilters,
        loadOrInvoiceOrDebtorOrStatus: value,
        status: "all"
      }));
    },
    [setFilters, refetchSummaryData]
  );
  const resetSearch = useCallback(() => {
    refetchSummaryData({
      debtor: filters.debtor,
      loadOrInvoiceOrDebtorOrStatus: ""
    });
    setFilters((prevFilters) => ({
      ...prevFilters,
      loadOrInvoiceOrDebtorOrStatus: "",
      status: "all"
    }));
  }, [setFilters, refetchSummaryData]);

  const handleFrTypeFilterChange = (selectedFrType: IOption) => {
    if (selectedFrType.value !== filters.contractType) {
      resetPagination();
      refetchSummaryData({
        status: filters.status,
        contractType: selectedFrType.value
      });
      setFilters((prevFilters) => ({
        ...prevFilters,
        status: filters.status,
        contractType: selectedFrType.value,
        orderBy: null
      }));
      setClearFrTypeFilter(true);
    }
  };

  const resetFrTypeFilter = () => {
    resetPagination();
    refetchSummaryData({
      status: filters.status,
      contractType: null
    });
    setFilters((prevFilters) => ({
      ...prevFilters,
      status: filters.status,
      contractType: null
    }));
    setClearFrTypeFilter(false);
  };

  const handleLinkClick = useCallback(() => {
    navigate(routes.HAULPAY_BROKER_MANAGE_PAY, {
      replace: true,
      state: {
        forceUpdate: true
      }
    });
  }, [navigate]);

  const frTypeOptions = FR_CONTRACT_TYPE.filter(
    (request) => !request.hidden && userPermission.available_contract_types[request.value]
  );
  const bulkFrTypeOptions = [
    NEW_FUNDING_REQUEST_TYPE[0],
    ...frTypeOptions.map((request) => ({
      id: request.id + 1,
      label: request.label,
      value: request.value
    }))
  ];

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        {isBroker && <Button onClick={goToCreateLoad}>Create Funding Request</Button>}
        <SearchInput
          initialValue={filters.loadOrInvoiceOrDebtorOrStatus || ""}
          onSearch={handleSearch}
          resetSearch={resetSearch}
        />
        {isBroker && isNonFactorRefactorEnabled && frTypeOptions.length > 0 && (
          <FRTypeFilterDropdown
            frTypeOptions={frTypeOptions}
            handleOnChange={handleFrTypeFilterChange}
            handleResetClick={resetFrTypeFilter}
            clearFilter={clearFrTypeFilter}
          />
        )}
      </div>
      {!!filters.debtorName && (
        <div className={styles.content}>
          <p>Displaying funding requests for {debtorName}</p>
          <button onClick={handleLinkClick} className={styles.clearFilter}>
            Clear Filter
          </button>
        </div>
      )}

      <Tab.Group
        selectedIndex={Object.keys(sortedSummary).indexOf(filters?.status ?? "")}
        onChange={(index) => {
          resetPagination();
          updateFilter("status", Object.keys(sortedSummary)[index]);
          trackAllEvents(`broker_managepay_${Object.keys(sortedSummary)[index] ?? "all"}`);
        }}>
        <StatusTabs tabsData={sortedSummary} summaryLabels={FR_LABELS} />

        <Tab.Panels>
          {Object.keys(sortedSummary).map((key) => (
            <Tab.Panel key={key} className="outline-none">
              {isBroker ? (
                <DataTable
                  headers={MANAGE_PAY_TABLE_HEADER_LIST}
                  data={data ?? []}
                  isShowChecked
                  handleSelectAll={handleSelectAll}
                  selectedList={selectedFRs}
                  handleClick={toggleSelection}
                  sortDisableList={["id"]}
                  totalCount={totalCount}
                  nextLink={pageInfo.hasNextPage}
                  prevLink={pageInfo.hasPreviousPage}
                  setPageSize={setPageSize}
                  goToPage={goToPage}
                  fetchPrevPage={loadPreviousPage}
                  fetchNextPage={loadNextPage}
                  loading={loading}
                  errorList={errorList}
                  loadMore
                  handleSortBy={handleSortBy}
                  selectAllLimit={SELECT_ALL_LIMIT}
                  customSelectAllCheckbox={CustomSelectAllCheckbox}
                  customTooltipCheckbox={CustomTooltipCheckbox}
                  isDisableCheckboxes={disableCheckboxes}
                />
              ) : (
                <DataTable
                  headers={carrierHeaders}
                  data={data}
                  sortDisableList={["id"]}
                  totalCount={totalCount}
                  nextLink={pageInfo.hasNextPage}
                  prevLink={pageInfo.hasPreviousPage}
                  setPageSize={setPageSize}
                  goToPage={goToPage}
                  fetchPrevPage={loadPreviousPage}
                  fetchNextPage={loadNextPage}
                  loading={loading}
                  infiniteScroll
                  handleSortBy={handleSortBy}
                  errorList={errorList}
                />
              )}
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
      {isBroker && selectedFRs.length > 0 ? (
        <form
          className={clsx(
            styles.footerContainer,
            !currentIsDark ? "drop-shadow-[0_-4px_4px_#B5B5B533]" : ""
          )}
          onSubmit={handleSubmit(onUpdateFR)}>
          <div className={styles.content}>
            <div className={styles.selectAll}>
              <span className="text-sm">Set Status</span>
              <Controller
                name="state"
                control={control}
                render={({ field: { value = NEW_STATUS_LIST[1], onChange } }) => (
                  <Select
                    value={value}
                    onChange={(value: IOption) => {
                      setTypeToUpdate("status");
                      onChange(value);
                    }}
                    options={
                      factoringProfile.enable_hold_review_funding_request_status
                        ? NEW_STATUS_LIST_WITH_HOLD
                        : NEW_STATUS_LIST
                    }
                    direction="up"
                    autoDirection={false}
                    className="!w-48"
                  />
                )}
              />
            </div>
            <Button
              type="submit"
              rounded
              className={clsx(
                styles.submitButton,
                typeToUpdate === "type"
                  ? "!bg-subtitle !ring-subtitle cursor-not-allowed"
                  : "bg-gradient-to-r from-primary to-secondary"
              )}
              disabled={typeToUpdate === "type"}
              loading={isSubmitting}>
              SUBMIT
            </Button>
          </div>
          <div className={clsx(styles.content, "items-center", "text-center", "flex-shrink-0")}>
            <span className="text-sm whitespace-nowrap">{selectedFRs.length} selected</span>
          </div>
          <div className={styles.content}>
            <div className={styles.selectAll}>
              <span className="text-sm">Set Type</span>
              <Controller
                name="type"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value}
                    onChange={(value: IOption) => {
                      setTypeToUpdate("type");
                      onChange(value);
                    }}
                    options={bulkFrTypeOptions}
                    direction="up"
                    autoDirection={false}
                    className="!w-48"
                  />
                )}
              />
            </div>

            <Button
              type="submit"
              rounded
              className={clsx(
                styles.submitButton,
                typeToUpdate === "status"
                  ? "!bg-subtitle !ring-subtitle cursor-not-allowed"
                  : "bg-gradient-to-l from-primary to-secondary"
              )}
              disabled={typeToUpdate === "status"}
              loading={isSubmitting}>
              SUBMIT
            </Button>
          </div>
        </form>
      ) : null}
      {isBroker && (
        <StatusModal
          title="Status Updated"
          content={`Successfully set ${typeToUpdate} to ${
            typeToUpdate === "status" ? getValues("state.label") : getValues("type.label")
          }.`}
          isModalOpen={isSuccessModalOpen}
          toggleModal={toggleSuccessModal}
          backdropClick={toggleSuccessModal}
        />
      )}
    </div>
  );
}
